import { Input, InputNumber, Select } from '@aigc/antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { Lens } from '@hookform/lenses';
import { useFieldArray } from '@hookform/lenses/rhf';
import {
  Button,
  ConfigProvider,
  Empty,
  Form,
  Mentions,
  Popconfirm,
  Space,
  Table,
  Typography,
  type GetRef,
  type UploadFile,
} from 'antd';
import { useImperativeHandle, useRef, type Ref } from 'react';
import { FormControl } from '../../../components/form-control';
import type { MultiPromptFormValues } from '../../video/schema';
import { getDefaultMultiPrompt } from '../../video/utils';
import type { GenerationType, VideoModelCode } from '../model-spec';
import { DurationTooltip } from './duration-tooltip';
import { PromptTooltip } from './prompt-tooltip';

export const MultiPromptControl = (props: {
  ref?: Ref<{ focus?: () => void }>;
  generationType?: GenerationType;
  images?: UploadFile[];
  lens: Lens<NonNullable<MultiPromptFormValues>>;
  modelLens?: Lens<VideoModelCode>;
}) => {
  const enableReference = props.generationType === 'reference';
  const tableRef = useRef<GetRef<typeof Table>>(null);
  const { fields, insert, remove, append } = useFieldArray(
    props.lens.interop(),
  );

  useImperativeHandle(props.ref, () => {
    return {
      focus() {
        tableRef.current?.nativeElement.focus();
        tableRef.current?.nativeElement.scrollIntoView();
      },
    };
  });

  return (
    <ConfigProvider
      renderEmpty={(componentName) => {
        if (componentName === 'Table') {
          return (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="请添加一个分镜"
            ></Empty>
          );
        }
      }}
    >
      <FormControl
        {...props.lens.interop()}
        exact
        render={() => (
          <Table
            ref={tableRef}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="请添加一个分镜"
                >
                  <Button
                    variant="dashed"
                    color="primary"
                    onClick={() => {
                      append(getDefaultMultiPrompt());
                    }}
                  >
                    添加分镜
                  </Button>
                </Empty>
              ),
            }}
            tableLayout="fixed"
            rowKey={(row) => row.id}
            size="small"
            dataSource={fields}
            bordered
            columns={[
              {
                title: (
                  <div className="flex items-center">
                    提示词
                    {enableReference && (
                      <PromptTooltip lens={props.modelLens}></PromptTooltip>
                    )}
                  </div>
                ),
                render: (_, __, index) => {
                  return (
                    <Form
                      layout="vertical"
                      rootClassName="grid grid-cols-4 gap-x-2"
                    >
                      <FormControl
                        {...props.lens
                          .focus(index)
                          .focus('visual_description')
                          .interop()}
                        label={
                          <span className="font-bold">镜号{index + 1}</span>
                        }
                        rootClassName="col-span-4"
                        render={({ field }) => {
                          if (enableReference) {
                            let imageIndex = 0;
                            let videoIndex = 0;

                            return (
                              <Mentions
                                {...field}
                                options={props.images?.map((item) => {
                                  if (item.name.endsWith('.mp4')) {
                                    videoIndex = videoIndex + 1;

                                    return {
                                      label: `视频${videoIndex}`,
                                      value: `视频${videoIndex}`,
                                      disabled: item.status !== 'done',
                                    };
                                  } else {
                                    imageIndex = imageIndex + 1;

                                    return {
                                      label: `图片${imageIndex}`,
                                      value: `图片${imageIndex}`,
                                      disabled: item.status !== 'done',
                                    };
                                  }
                                })}
                                autoSize={{ minRows: 3 }}
                                placeholder="请输入视频生成提示词，部分模型可以通过 @ 引用参考图"
                              ></Mentions>
                            );
                          }

                          return (
                            <Input.TextArea
                              {...field}
                              autoSize={{ minRows: 3 }}
                              placeholder="请输入视频生成提示词"
                            ></Input.TextArea>
                          );
                        }}
                      ></FormControl>
                      <FormControl
                        {...props.lens.focus(index).focus('duration').interop()}
                        label="时长"
                        tooltip={{
                          classNames: { root: 'min-w-fit' },
                          title: (
                            <DurationTooltip
                              generationType={props.generationType}
                            ></DurationTooltip>
                          ),
                        }}
                        render={({ field }) => {
                          return (
                            <InputNumber
                              classNames={{ root: 'w-full' }}
                              {...field}
                              min={0}
                              controls={false}
                              onFocus={(e) => {
                                e.target.select();
                              }}
                              suffix="秒"
                            ></InputNumber>
                          );
                        }}
                      ></FormControl>
                      <FormControl
                        {...props.lens
                          .focus(index)
                          .focus('shot_type')
                          .interop()}
                        label="镜头"
                        render={({ field }) => {
                          return (
                            <Input {...field} value={field.value ?? ''}></Input>
                          );
                        }}
                      ></FormControl>
                      <FormControl
                        {...props.lens
                          .focus(index)
                          .focus('shot_purpose')
                          .interop()}
                        label="镜头目的"
                        render={({ field }) => {
                          return (
                            <Input {...field} value={field.value ?? ''}></Input>
                          );
                        }}
                      ></FormControl>
                      <FormControl
                        {...props.lens
                          .focus(index)
                          .focus('camera_movement')
                          .interop()}
                        label="运镜"
                        render={({ field }) => {
                          return (
                            <Input {...field} value={field.value ?? ''}></Input>
                          );
                        }}
                      ></FormControl>
                      <FormControl
                        {...props.lens
                          .focus(index)
                          .focus('visual_keywords')
                          .interop()}
                        label="画面关键词"
                        tooltip={{
                          classNames: { root: 'min-w-fit' },
                          title: (
                            <>
                              输入任意内容后按
                              <Typography.Text keyboard>回车</Typography.Text>
                              ，即可添加新关键词
                            </>
                          ),
                        }}
                        render={({ field }) => {
                          return (
                            <Select
                              {...field}
                              mode="tags"
                              maxTagCount="responsive"
                            ></Select>
                          );
                        }}
                      ></FormControl>
                      <FormControl
                        {...props.lens
                          .focus(index)
                          .focus('dialogue_subtitle_emotion')
                          .interop()}
                        className="col-span-3"
                        label="台词/字幕/情绪"
                        render={({ field }) => {
                          return (
                            <Input {...field} value={field.value ?? ''}></Input>
                          );
                        }}
                      ></FormControl>
                    </Form>
                  );
                },
              },
              {
                title: '操作',
                width: 48,
                className: 'align-top',
                render: (_, __, index) => {
                  return (
                    <Form.Item>
                      <Space orientation="vertical">
                        <Button
                          variant="filled"
                          color="default"
                          onClick={() => {
                            insert(index + 1, {
                              ...getDefaultMultiPrompt(),
                              duration: null!,
                            });
                          }}
                          icon={<PlusOutlined />}
                        ></Button>
                        <Popconfirm
                          title="删除"
                          description="确定删除改提示词？"
                          onConfirm={() => {
                            remove(index);
                          }}
                        >
                          <Button
                            variant="filled"
                            color="danger"
                            icon={<DeleteOutlined />}
                          ></Button>
                        </Popconfirm>
                      </Space>
                    </Form.Item>
                  );
                },
              },
            ]}
            pagination={false}
          ></Table>
        )}
      ></FormControl>
    </ConfigProvider>
  );
};
